// Currency prefixes are tenant-configured and frequently stored as HTML
// entities (e.g. "R&nbsp;", "&#8358;&nbsp;", "&pound;", "P&nbsp;"). We must
// render the intended glyph, but NEVER via an HTML sink (the value can reach
// the public storefront from a remote site-config → stored-XSS risk).
//
// Approach: decode entities to real Unicode characters here, then render the
// result as a plain React text child. React escapes on output, so any markup
// that survives decoding (e.g. "<script>") is shown literally, never executed.

const NAMED_ENTITIES: Record<string, string> = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' ',
    pound: '£',
    euro: '€',
    cent: '¢',
    yen: '¥',
    curren: '¤',
    dollar: '$',
};

/**
 * Decode HTML entities (numeric `&#8358;`, hex `&#x20A6;`, and a small set of
 * named entities) in a currency prefix to their Unicode characters. Returns a
 * plain string safe to render as a React text child. Length-capped defensively.
 */
export function decodeCurrencyPrefix(raw?: string | null): string {
    if (!raw) return '';
    const capped = String(raw).slice(0, 40);
    return capped.replace(/&(#x[0-9a-fA-F]+|#[0-9]+|[a-zA-Z]+);/g, (match, body) => {
        if (body[0] === '#') {
            const isHex = body[1] === 'x' || body[1] === 'X';
            const code = parseInt(body.slice(isHex ? 2 : 1), isHex ? 16 : 10);
            if (Number.isFinite(code) && code > 0 && code <= 0x10FFFF) {
                try {
                    return String.fromCodePoint(code);
                } catch {
                    return match;
                }
            }
            return match;
        }
        const named = NAMED_ENTITIES[body.toLowerCase()];
        return named !== undefined ? named : match;
    });
}

/**
 * Format an amount for display next to a currency prefix: always exactly two
 * decimals, comma thousands — the same shape as the API's `price_display`
 * (PHP number_format). Client-computed totals (cart sums) must go through
 * this; bare toLocaleString() leaks up to three decimals of float dust.
 */
export function formatMoney(value: number): string {
    return value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}
